import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type { UserListItemDto } from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export const useUserQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  return {
    listInfinite: (query: ApiQueryParams<"getUsersV1">) => {
      const limit = 25;

      return infiniteQueryOptions({
        initialPageParam: 0,
        queryKey: QUERY_KEYS.USERS_QUERY(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getUsersV1({
              ...query,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then((items) => commonAPIDataSelector(items)),
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        placeholderData: keepPreviousData,
      });
    },
    list: ({ params }: { params: ApiQueryParams<"getUsersV1"> }) =>
      queryOptions({
        queryKey: QUERY_KEYS.USERS_QUERY(projectId, params),
        queryFn: () => api.getUsersV1(params),
        select: (response) => commonAPIDataSelector(response).items,
      }),
    exportList: (query: ApiQueryParams<"getUsersV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.USERS_EXPORT(projectId, query),
        queryFn: async () => {
          if (query.Statuses?.length === 0) {
            return [];
          }

          const limit = 10000;
          let offset = 0;
          let hasMore = true;
          const users: UserListItemDto[] = [];

          while (hasMore) {
            const response = await api
              .getUsersV1({
                ...query,
                Offset: offset,
                Limit: limit,
              })
              .then((items) => commonAPIDataSelector(items));

            users.push(...response.items);
            hasMore = response.hasMore;
            offset += limit;
          }

          return users;
        },
      }),
    lookupInfinite: (query: ApiQueryParams<"getUsersLookupV1">) => {
      const limit = 10;

      return infiniteQueryOptions({
        initialPageParam: 0,
        queryKey: QUERY_KEYS.USERS_LOOKUP(query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getUsersLookupV1({
              ...query,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then((res) => commonAPIDataSelector(res)),
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 5000,
      });
    },
    details: ({ userId }: { userId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.USER_DETAILS(projectId, userId),
        queryFn: () => api.getUsersDetailsV1(userId),
        select: commonAPIDataSelector,
      }),
    listLinkedProjects: ({ userId }: { userId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.USER_PROJECTS(projectId, userId),
        queryFn: () => api.getUsersProjectsV1(userId),
        select: commonAPIDataSelector,
      }),
    signUpReminders: ({ userId }: { userId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.USER_SIGNUP_REMINDERS(projectId, userId),
        queryFn: () => api.getUsersUserSignupRemindersV1(userId),
        select: commonAPIDataSelector,
      }),
    notificationSettings: ({ userId }: { userId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.NOTIFICATION_SETTINGS_USER(projectId, userId),
        queryFn: () => api.getNotificationSettingsDetailsV1(userId),
        select: commonAPIDataSelector,
      }),
    notificationSettingsDefaults: () =>
      queryOptions({
        queryKey: QUERY_KEYS.NOTIFICATION_SETTINGS_DEFAULT(),
        queryFn: () => api.getNotificationSettingsDefaultsV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
        gcTime: Infinity,
      }),
    listAdmins: (projectIdOverride?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.USERS_ADMINS(projectIdOverride ?? projectId),
        queryFn: () =>
          api.getUsersAdminsV1(projectIdOverride ? { headers: { [ProjectHeaderName]: projectIdOverride } } : undefined),
        select: commonAPIDataSelector,
      }),
    iloqStatus: ({ userId }: { userId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.USER_ILOQ_STATUS(projectId, userId),
        queryFn: () => api.getIloqKeyUsersDetailsV1(userId),
        select: commonAPIDataSelector,
      }),
  };
};
